import React, { useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function CustomerLogin() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [step, setStep] = useState('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [otp, setOtp] = useState('');
  const [otpHint, setOtpHint] = useState('');

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/customer/login`, { email, password });
      const sent = response.data.message || 'Verification code sent';
      toast.success(sent);
      setOtpHint(sent);
      setStep('otp');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/customer/verify-otp`, { email, otp });
      toast.success('Login successful!');
      localStorage.setItem('customer_token', response.data.access_token);
      localStorage.setItem('customer_user', JSON.stringify(response.data.user));
      navigate('/customer/products');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'OTP verification failed');
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    setResending(true);
    try {
      const response = await axios.post(`${API}/customer/resend-otp`, { email });
      const sent = response.data.message || 'A new code has been sent';
      toast.success(sent);
      setOtpHint(sent);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not resend the code');
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-white p-4">
      <Card className="w-full max-w-md border-2 border-[#8FC043]" data-testid="customer-login-card">
        <CardHeader className="space-y-4">
          <div className="flex justify-center">
            <img src="/himan-logo.png" alt="Himan Logo" className="h-20 w-20 object-contain" />
          </div>
          <CardTitle className="text-2xl font-bold text-center">Customer Login</CardTitle>
          <CardDescription className="text-center">
            {step === 'login' ? 'Welcome back! Login to continue shopping' : (otpHint || 'Enter the verification code we sent you')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {step === 'otp' ? (
            <form onSubmit={handleVerifyOtp} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="otp">Verification Code</Label>
                <Input
                  id="otp"
                  data-testid="otp-input"
                  type="text"
                  placeholder="Enter 6-digit code"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value)}
                  maxLength={6}
                  required
                />
              </div>
              <Button data-testid="verify-otp-button" type="submit" className="w-full" disabled={loading}>
                {loading ? <Loader2 className="animate-spin" /> : 'Verify & Login'}
              </Button>
              <button
                type="button"
                data-testid="resend-otp-link"
                className="w-full text-sm text-gray-500 hover:text-[#8FC043] underline"
                onClick={handleResendOtp}
                disabled={resending}
              >
                {resending ? 'Sending…' : "Didn't get the code? Resend"}
              </button>
              <Button type="button" variant="outline" className="w-full" onClick={() => { setStep('login'); setOtp(''); }}>
                Back
              </Button>
            </form>
          ) : (
          <form onSubmit={handleLogin} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                data-testid="email-input"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                data-testid="password-input"
                type="password"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>
            <Button data-testid="login-submit-button" type="submit" className="w-full" disabled={loading}>
              {loading ? <Loader2 className="animate-spin" /> : 'Login'}
            </Button>
            <div className="text-center text-sm">
              Don't have an account?{' '}
              <button
                type="button"
                onClick={() => navigate('/customer/register')}
                className="text-[#8FC043] hover:underline font-semibold"
              >
                Register here
              </button>
            </div>
          </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
